<template>
  <div class="billing-add-payment">
    <div class="billing-add-payment__heading">
      <h3>{{ _text.primary }}</h3>
      <h5>{{ _text.secondary }}</h5>
    </div>

    <WithLoader :isLoading="isPlaidSaving">
      <div v-if="currentStep === 1" class="billing-add-payment__cards">
        <VCard
          id="credit"
          title="Debit card"
          :icon="cardIcon"
          :iconActive="cardIconActive"
          @click="onCardClick"
        />

        <PlaidLink
          :env="plaidMode"
          clientName="Inkit"
          product="auth"
          webhook="https://localhost:8080"
          :publicKey="plaidKey"
          :onSuccess="onPlaidSuccess"
        >
          <VCard
            id="bankAccount"
            title="Bank account"
            :icon="bankIcon"
            :iconActive="bankIconActive"
            @click="onCardClick"
          />
        </PlaidLink>
      </div>
    </WithLoader>

    <div v-if="currentStep === 2" class="add-credit-card__wrapper">
      <WithLoader :isLoading="isCardAdding">
        <BillingCreditCardForm @updstate="_ccFormReadyHandler" />
      </WithLoader>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions } from "vuex";

// components
import VCard from "@/components/VCard";
import BillingCreditCardForm from "@/components/BillingCreditCardForm";
import PlaidLink from "@/components/PlaidLink";

// icons
import bankIcon from "@/assets/images/billing/Bank.svg";
import bankIconActive from "@/assets/images/billing/Bank-active.svg";
import cardIcon from "@/assets/images/billing/Card.svg";
import cardIconActive from "@/assets/images/billing/Card-active.svg";

import withToasted from "@/mixins/withToasted";

import { plaidKey, plaidMode } from "@/config";

export default Vue.component("BillingAddPaymentMethodForm", {
  props: {
    buttons: VueTypes.any,
    addNewPaymentOpened: VueTypes.bool
  },

  components: {
    VCard,
    BillingCreditCardForm,
    PlaidLink
  },

  mixins: [withToasted],

  data() {
    return {
      // steps for displaying different screens
      // and add ability to go back & forth
      // 1 - main screen with choosing cards
      // 2 - add debit card screen
      currentStep: 1,
      initialButtons: this.buttons,
      isDCFormReady: false,
      values: null,
      isCardAdding: false,

      // ACH data
      plaidToken: null,
      chosenBankAccount: null,
      isPlaidSaving: false,
      bankAccountsList: [], // accounts list for current plaid bank

      bankIcon,
      cardIcon,
      bankIconActive,
      cardIconActive,
      plaidKey,
      plaidMode
    };
  },

  methods: {
    _emitParentAddPaymentScreen(status) {
      this.$emit("update:addpayment", status);
    },

    _emitParentButtons() {
      this.$emit("update:buttons", this._stepButtons);
    },

    _goBack() {
      this.currentStep = this.currentStep - 1;
    },

    /**
     * Handler for getting child value whether is form ready to be submitted
     * @param isReady
     * @param values
     * @private
     */
    _ccFormReadyHandler(isReady, values) {
      this.isDCFormReady = isReady;
      this.values = { ...values };
      this._emitParentButtons();
    },

    _onCancel() {
      this.values = null;
      this.isDCFormReady = false;
      this._emitParentButtons();
    },

    _resetScreen() {
      this._emitParentAddPaymentScreen(false);
      this.currentStep = 0;

      this._onCancel();
    },

    _getText(step) {
      switch (step) {
        case 1:
          return {
            primary: "Add payment methods",
            secondary: "Please select the payment method you would like to add"
          };

        // add credit cards step
        case 2:
          return {
            primary: "Add debit card",
            secondary: "Please enter your card information below"
          };
      }
    },

    onPlaidSuccess(token, accountInfo) {
      this.isPlaidSaving = true;
      this.savePlaidAccount({
        token,
        account_id: accountInfo.account.id,
        onSuccess: () => {
          this.isPlaidSaving = false;
          this._resetScreen();
          this.toast("Bank account has been saved.");
        },

        onError: e => {
          this.isPlaidSaving = false;
          const err = e.description
            ? e.description
            : "Something went wrong. Please, try again later";
          this.toast(err, "error");
        }
      });
    },

    onCardClick(key) {
      if (key === "credit") {
        this.currentStep = 2;
        this._emitParentButtons();
      }
    },

    addCard() {
      const { number, exp, cvv: cvc } = this.values;
      const data = {
        number,
        exp,
        cvc
      };

      this.isCardAdding = true;

      this.createCreditCard({
        data,
        onSuccess: card => {
          this.toast(
            `Card ended by ${card.last4} has been added successfully.`,
            "success",
            {
              duration: 4000
            }
          );

          this._resetScreen();
          this.isCardAdding = false;
        },

        onError: e => {
          let msg =
            "Something went wrong during adding new card. Please, try again later";

          if (e.type === "card_error") {
            msg = e.message;
          }

          this.toast(msg, "error");
          this.isCardAdding = false;
        }
      });
    },

    ...mapActions("billing", ["createCreditCard", "savePlaidAccount"])
  },

  computed: {
    _text() {
      return this._getText(this.currentStep);
    },

    _stepButtons() {
      if (this.currentStep === 1) {
        return [
          {
            label: "Cancel",
            value: "cancel",
            type: "primary",
            size: "sm",
            onClickHandler: this._resetScreen,
            inverted: true,
            extraClass: "payment-add-primary-button",
            disabled: this.isCardAdding
          }
        ];
      } else if (this.currentStep === 2) {
        let form = [
          {
            label: "Back",
            value: "back",
            type: "primary",
            size: "sm",
            onClickHandler: () => {
              this._goBack();
              this._emitParentButtons();
            },
            inverted: true,
            extraClass: "credit-cards-back",
            disabled: this.isCardAdding
          },
          {
            label: "Cancel",
            value: "cancel",
            type: "primary",
            size: "sm",
            onClickHandler: this._resetScreen,
            inverted: true,
            extraClass: "payment-add-primary-button",
            disabled: this.isCardAdding
          }
        ];

        if (this.isDCFormReady) {
          form = [
            {
              label: "Cancel",
              value: "cancel",
              type: "primary",
              size: "sm",
              onClickHandler: this._resetScreen,
              inverted: true,
              extraClass: "credit-cards-back",
              disabled: this.isCardAdding
            },
            {
              label: "Add this card",
              value: "add",
              type: "primary",
              size: "sm",
              onClickHandler: this.addCard,
              extraClass: "payment-add-primary-button",
              disabled: this.isCardAdding
            }
          ];
        }

        return form;
      } else {
        return this.initialButtons;
      }
    }
  },

  mounted() {
    this._emitParentButtons();
  }
});
</script>

<style lang="scss" scoped>
$ns: "billing-add-payment";

.#{$ns} {
  &__heading {
    text-align: center;

    h3 {
      font-family: "Lato-Bold", sans-serif;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 13px;
      color: $main-text-color;
    }

    h5 {
      font-family: "Lato-Regular", sans-serif;
      font-size: 18px;
      line-height: 22px;
      color: $secondary-text-color;
    }

    padding-bottom: 50px;
  }

  &__cards {
    display: flex;
    align-items: center;
    justify-content: center;

    > div:first-child {
      margin-right: 70px;
    }

    /deep/ .credit {
      .item-logo--top,
      .item-logo--bottom {
        width: 50px;
        height: 45px;
      }
    }

    /deep/ .bankAccount {
      .item-logo--top,
      .item-logo--bottom {
        width: 45px;
        height: 45px;
      }
    }

    @media screen and (max-width: $tablet-screen) {
      flex-direction: column;
      > div:first-child {
        margin-right: 0;
        margin-bottom: 20px;
      }
    }
  }

  .add-credit-card__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>

<style lang="scss">

</style>
